import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HttpsOutlinedIcon from "@mui/icons-material/HttpsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

export type LoginStep = "CREDENTIALS" | "OTP_SELECT" | "OTP_VERIFY";
export type OtpChannel = "EMAIL" | "SMS";

export interface MFALoginFormProps {
  step: LoginStep;
  email: string;
  password: string;
  otp: string;
  otpChannel: OtpChannel;
  loading?: boolean;
  resendCountdown?: number;
  passwordErrors?: string[];
  regulatoryAccepted?: boolean;
  showPassword?: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onOtpChange: (value: string) => void;
  onOtpChannelChange: (value: OtpChannel) => void;
  onTogglePassword: () => void;
  onCredentialSubmit: () => void;
  onSendOtp: () => void;
  onVerifyOtp: () => void;
  onBackToCredentials: () => void;
  onRegulatoryAcceptedChange: (value: boolean) => void;
  onForgotPassword?: () => void;
}

const primaryButtonSx = {
  height: 48,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 700,
  boxShadow: "none",
};

const secondaryButtonSx = {
  height: 46,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 700,
};

function StepHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <Stack spacing={0.5} sx={{ mb: 0.5 }}>
      <Typography
        sx={{
          fontSize: 28,
          lineHeight: 1.2,
          fontWeight: 800,
          color: "#17324D",
          letterSpacing: "-0.02em",
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          fontSize: 14,
          color: "#66788A",
          lineHeight: 1.6,
        }}
      >
        {subtitle}
      </Typography>
    </Stack>
  );
}

function StepIndicator({ step }: { step: LoginStep }) {
  const items: Array<{ key: LoginStep; label: string }> = [
    { key: "CREDENTIALS", label: "Identifiants" },
    { key: "OTP_SELECT", label: "Canal" },
    { key: "OTP_VERIFY", label: "Vérification" },
  ];

  const activeIndex = items.findIndex((item) => item.key === step);

  return (
    <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
      {items.map((item, index) => {
        const active = index <= activeIndex;
        return (
          <Box key={item.key} sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: 11,
                mb: 0.75,
                color: active ? "#116B43" : "#8A9BAB",
                fontWeight: active ? 700 : 600,
              }}
            >
              {item.label}
            </Typography>
            <Box
              sx={{
                height: 6,
                borderRadius: "8px",
                bgcolor: active ? "#116B43" : "#E7EDF2",
                transition: "all .2s ease",
              }}
            />
          </Box>
        );
      })}
    </Stack>
  );
}

function SecurityNote() {
  return (""
    // <Alert
    //   icon={<ShieldOutlinedIcon fontSize="inherit" />}
    //   severity="success"
    //   sx={{
    //     borderRadius: "8px",
    //     bgcolor: "#F3FBF6",
    //     color: "#184B30",
    //     border: "1px solid #D7EFDF",
    //     "& .MuiAlert-icon": {
    //       color: "#116B43",
    //     },
    //   }}
    // >
    //   Accès réservé au personnel autorisé. Toutes les connexions sont journalisées.
    // </Alert>
  );
}

export default function MFALoginForm(props: any) {
  const {
    step,
    email,
    password,
    otp,
    otpChannel,
    loading = false,
    resendCountdown = 0,
    passwordErrors = [],
    showPassword = false,
    onEmailChange,
    onPasswordChange,
    onOtpChange,
    onOtpChannelChange,
    onTogglePassword,
    onCredentialSubmit,
    onSendOtp,
    onVerifyOtp,
    onBackToCredentials,
    onRegulatoryAcceptedChange,
    onForgotPassword,
  } = props;

  return (
    <Stack spacing={2.5}>
      <StepIndicator step={step} />
      <SecurityNote />

      {step === "CREDENTIALS" && (
        <>
          {/* <StepHeader
            title="Connexion sécurisée"
            subtitle="Accédez au portail BPMMS de la SONABHY avec vos identifiants institutionnels."
          /> */}

          {/* <Alert
            severity="info"
            sx={{
              borderRadius: "8px",
              bgcolor: "#F7FAFC",
              border: "1px solid #E3EAF0",
              color: "#415A6B",
              "& .MuiAlert-icon": {
                color: "#116B43",
              },
            }}
          > 
            En accédant à ce portail, vous reconnaissez les règles d’accès, de sécurité
            et de confidentialité applicables au système.
          </Alert>*/}

          <TextField
            fullWidth
            label="Adresse e-mail ou identifiant"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            autoComplete="username"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: "#6B7C8C" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />

          <TextField
            fullWidth
            label="Mot de passe"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: "#6B7C8C" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={onTogglePassword} edge="end">
                    {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />

          {passwordErrors.length > 0 && (
            <Box
              sx={{
                p: 2,
                borderRadius: "8px",
                bgcolor: "#FFF8F7",
                border: "1px solid #F6D5D1",
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#9B2C1D", mb: 0.5 }}>
                Le mot de passe ne respecte pas encore la politique requise :
              </Typography>
              {passwordErrors.map((err:any) => (
                <Typography key={err} sx={{ fontSize: 12, color: "#9B2C1D", lineHeight: 1.5 }}>
                  • {err}
                </Typography>
              ))}
            </Box>
          )}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
          >
            <Button
              variant="text"
              onClick={onForgotPassword}
              sx={{ textTransform: "none", fontWeight: 700, color: "#116B43", px: 0.5 }}
            >
              Mot de passe oublié ?
            </Button>

            <Typography sx={{ fontSize: 12, color: "#7B8B98" }}>
              Authentification multifacteur activée
            </Typography>
          </Stack>

          <Button
            fullWidth
            variant="contained"
            disabled={!email || !password || passwordErrors.length > 0 || loading}
            onClick={onCredentialSubmit}
            sx={{
              ...primaryButtonSx,
              bgcolor: "#116B43",
              "&:hover": { bgcolor: "#0B4F2D" },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Continuer"}
          </Button>
        </>
      )}

      {step === "OTP_SELECT" && (
        <>
          {/* <StepHeader
            title="Choix du canal de vérification"
            subtitle="Sélectionnez le canal par lequel vous souhaitez recevoir votre code à usage unique."
          /> */}
          <Typography sx={{ fontSize: 13, color: "#66788A" }}>
            Choisissez le canal de vérification.
          </Typography>

          <Stack spacing={1.5}>
            <Button
              fullWidth
              variant={otpChannel === "EMAIL" ? "contained" : "outlined"}
              onClick={() => onOtpChannelChange("EMAIL")}
              startIcon={<EmailOutlinedIcon />}
              sx={{
                ...secondaryButtonSx,
                justifyContent: "flex-start",
                bgcolor: otpChannel === "EMAIL" ? "#116B43" : undefined,
                borderColor: "#D5DEE6",
                color: otpChannel === "EMAIL" ? "#fff" : "#17324D",
              }}
            >
              Recevoir le code par e-mail
            </Button>

            <Button
              fullWidth
              variant={otpChannel === "SMS" ? "contained" : "outlined"}
              onClick={() => onOtpChannelChange("SMS")}
              startIcon={<PhoneAndroidOutlinedIcon />}
              sx={{
                ...secondaryButtonSx,
                justifyContent: "flex-start",
                bgcolor: otpChannel === "SMS" ? "#116B43" : undefined,
                borderColor: "#D5DEE6",
                color: otpChannel === "SMS" ? "#fff" : "#17324D",
              }}
            >
              Recevoir le code par SMS
            </Button>
          </Stack>

          <Divider />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onBackToCredentials}
              sx={{ ...secondaryButtonSx, borderColor: "#D5DEE6", color: "#17324D" }}
            >
              Retour
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={onSendOtp}
              sx={{
                ...primaryButtonSx,
                bgcolor: "#116B43",
                "&:hover": { bgcolor: "#0B4F2D" },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Envoyer le code"}
            </Button>
          </Stack>
        </>
      )}

      {step === "OTP_VERIFY" && (
        <>
          {/* <StepHeader
            title="Vérification du code"
            subtitle={`Saisissez le code reçu par ${otpChannel === "EMAIL" ? "e-mail" : "SMS"} pour finaliser la connexion.`}
          /> */}
          <Typography sx={{ fontSize: 13, color: "#66788A" }}>
            Saisissez le code reçu pour finaliser la connexion.
          </Typography>

          <TextField
            fullWidth
            label="Code de vérification"
            value={otp}
            onChange={(e) => onOtpChange(e.target.value)}
            inputProps={{ maxLength: 8, inputMode: "numeric" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <HttpsOutlinedIcon sx={{ color: "#6B7C8C" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={1}
          >
            <Button
              variant="text"
              onClick={onSendOtp}
              disabled={loading || resendCountdown > 0}
              sx={{ textTransform: "none", fontWeight: 700, color: "#116B43", px: 0.5 }}
            >
              {resendCountdown > 0
                ? `Renvoyer dans ${resendCountdown}s`
                : "Renvoyer le code"}
            </Button>

            <Button
              variant="text"
              onClick={onBackToCredentials}
              sx={{ textTransform: "none", fontWeight: 700, color: "#17324D", px: 0.5 }}
            >
              Changer d’identifiants
            </Button>
          </Stack>

          <Button
            fullWidth
            variant="contained"
            disabled={!otp || loading}
            onClick={onVerifyOtp}
            sx={{
              ...primaryButtonSx,
              bgcolor: "#116B43",
              "&:hover": { bgcolor: "#0B4F2D" },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Vérifier et se connecter"}
          </Button>
        </>
      )}

      <Box
        sx={{
          pt: 1,
          borderTop: "1px solid #EEF2F5",
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#8A9BAB", lineHeight: 1.7 }}>
          Assistance technique · support portail · Connexion sécurisée
        </Typography>
      </Box>
    </Stack>
  );
}
